// Menu op het native popover-attribuut (patroon 7): licht wegklikken en Esc
// doet de browser; pijltjes, focus terug naar de knop en de plek op het
// scherm doen we zelf. Zonder popover-API (jsdom in de tests) werkt hetzelfde
// menu met een gewone klik-buiten.

import { Check, ChevronDown } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import { Icoon, Veld } from "./basis";

type PopoverEl = HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void };

/** Kent de browser het popover-attribuut? (jsdom niet: dan een gewone laag.) */
const POPOVER = typeof HTMLElement !== "undefined" && typeof (HTMLElement.prototype as PopoverEl).showPopover === "function";

export function Menu({
  inhoud,
  knopKlasse = "s-menu-knop",
  aan = false,
  label,
  boven = false,
  lat,
  children,
}: {
  /** Wat er op de knop staat. */
  inhoud: ReactNode;
  knopKlasse?: string;
  /** Er staat iets aan in dit menu (de knop kleurt mee). */
  aan?: boolean;
  /** Toegankelijke naam als `inhoud` geen tekst is. */
  label?: string;
  /** Opent boven de knop (profielmenu onderin de zijbalk). */
  boven?: boolean;
  /** Haakje voor de schermproef op de knop (bv. "meer-filters"). */
  lat?: string;
  children: ReactNode | ((sluit: () => void) => ReactNode);
}) {
  const [open, setOpen] = useState(false);
  const knop = useRef<HTMLButtonElement>(null);
  const menu = useRef<PopoverEl>(null);
  const zojuistDicht = useRef(0);
  const id = useId();

  const sluit = (focusTerug = true) => {
    setOpen(false);
    if (focusTerug) knop.current?.focus();
  };

  useLayoutEffect(() => {
    const el = menu.current;
    if (!open || !el) return;
    if (typeof el.showPopover === "function") {
      try {
        el.showPopover();
      } catch {
        /* al open */
      }
    }
    // Het menu hangt aan zijn knop (owner 12-10: "het moet hieraan vast zitten"):
    // bij scrollen en schalen schuift het mee; verdwijnt de knop uit beeld, dan
    // gaat het menu dicht in plaats van los te zweven.
    const plaats = () => {
      const r = knop.current?.getBoundingClientRect();
      if (!r) return;
      if (r.bottom < 0 || r.top > window.innerHeight) {
        setOpen(false);
        return;
      }
      const b = el.offsetWidth || 260;
      const x = Math.max(8, Math.min(r.left, window.innerWidth - b - 8));
      el.style.setProperty("--menu-x", `${x}px`);
      if (boven) el.style.setProperty("--menu-y", `${Math.max(8, r.top - el.offsetHeight - 4)}px`);
      else el.style.setProperty("--menu-y", `${r.bottom + 4}px`);
    };
    plaats();
    el.querySelector<HTMLElement>('input, [role^="menuitem"]:not(:disabled)')?.focus({ preventScroll: true });
    let raam = 0;
    const volg = () => {
      cancelAnimationFrame(raam);
      raam = requestAnimationFrame(plaats);
    };
    window.addEventListener("scroll", volg, true);
    window.addEventListener("resize", volg);
    return () => {
      cancelAnimationFrame(raam);
      window.removeEventListener("scroll", volg, true);
      window.removeEventListener("resize", volg);
    };
  }, [open, boven]);

  // Native popover: licht wegklikken en Esc melden zich als "toggle".
  useEffect(() => {
    const el = menu.current;
    if (!open || !el) return;
    const opToggle = (e: Event) => {
      if ((e as Event & { newState?: string }).newState === "closed") {
        zojuistDicht.current = Date.now();
        setOpen(false);
      }
    };
    el.addEventListener("toggle", opToggle);
    // Zonder popover-API: zelf klik-buiten.
    const zonderApi = typeof el.showPopover !== "function";
    const buiten = (e: MouseEvent) => {
      if (!el.contains(e.target as Node) && !knop.current?.contains(e.target as Node)) setOpen(false);
    };
    if (zonderApi) document.addEventListener("mousedown", buiten);
    return () => {
      el.removeEventListener("toggle", opToggle);
      if (zonderApi) document.removeEventListener("mousedown", buiten);
    };
  }, [open]);

  const toets = (e: KeyboardEvent) => {
    const items = [...(menu.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled)') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      sluit();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(i + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(i - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items.at(-1)?.focus();
    }
  };

  return (
    <>
      <button
        ref={knop}
        type="button"
        className={`${knopKlasse}${aan ? ` ${knopKlasse}--aan` : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={label}
        data-lat={lat}
        onClick={() => {
          if (Date.now() - zojuistDicht.current < 250) return;
          setOpen((o) => !o);
        }}
      >
        {inhoud}
      </button>
      {open && (
        <div
          ref={menu}
          id={id}
          // React 19 kent het popover-attribuut.
          popover={POPOVER ? "auto" : undefined}
          role="menu"
          aria-label={label}
          className="s-menu"
          data-lat="zweeft"
          onKeyDown={toets}
        >
          {typeof children === "function" ? children(() => sluit()) : children}
        </div>
      )}
    </>
  );
}

/** De knopinhoud van een filtermenu: label, eventueel een getal, pijltje. */
export function MenuKnopTekst({ label, tel }: { label: string; tel?: number }) {
  return (
    <>
      {label}
      {tel ? <span className="s-getal">· {tel}</span> : null}
      <Icoon als={ChevronDown} klein />
    </>
  );
}

export function MenuItem({
  onKies,
  children,
  icoon,
  disabled,
  uitleg,
}: {
  onKies: () => void;
  children: ReactNode;
  icoon?: ReactNode;
  disabled?: boolean;
  uitleg?: string;
}) {
  return (
    <button type="button" role="menuitem" className="s-menu__item" disabled={disabled} title={uitleg} onClick={onKies}>
      {icoon}
      <span className="s-menu__label">{children}</span>
    </button>
  );
}

export type Optie = { waarde: string; label: string; tel?: number; uit?: string; /** Eén zin uitleg onder het label. */ uitleg?: string };

/** Een lijstje met vinkjes en tellingen (meerkeuze). Lijstjes boven sliders. */
export function MenuKeuzes({
  kop,
  opties,
  gekozen,
  onWissel,
  notitie,
  zoeken = false,
  onWis,
}: {
  kop?: string;
  /** "Wissen" naast de kop zodra er iets gekozen is. */
  onWis?: () => void;
  opties: readonly Optie[];
  gekozen: readonly string[];
  onWissel: (waarde: string) => void;
  notitie?: string;
  /** Een zoekveldje boven een lange lijst. */
  zoeken?: boolean;
}) {
  const [q, setQ] = useState("");
  const zicht = q ? opties.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : opties;
  return (
    <div role="group" aria-label={kop}>
      {kop && (
        <div className="s-menu__kop">
          <span>{kop}</span>
          {onWis && gekozen.length > 0 && (
            <button type="button" className="s-menu__wis" onClick={onWis}>
              Wissen
            </button>
          )}
        </div>
      )}
      {zoeken && (
        <div className="s-menu__notitie">
          <Veld type="text" aria-label={`Zoek in ${kop ?? "de lijst"}`} placeholder="Zoeken…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      )}
      {zicht.map((o) => {
        const aan = gekozen.includes(o.waarde);
        return (
          <button
            key={o.waarde}
            type="button"
            role="menuitemcheckbox"
            aria-checked={aan}
            className="s-menu__item"
            disabled={!!o.uit}
            title={o.uit}
            onClick={() => onWissel(o.waarde)}
          >
            <span className={`s-menu__vak${aan ? " s-menu__vak--aan" : ""}`} aria-hidden>
              {aan && <Icoon als={Check} klein />}
            </span>
            <span className="s-menu__label">
              {o.label}
              {o.uitleg && <span className="s-menu__uitleg">{o.uitleg}</span>}
            </span>
            {o.tel !== undefined && <span className="s-menu__tel">{o.tel.toLocaleString("nl-NL")}</span>}
          </button>
        );
      })}
      {zicht.length === 0 && <div className="s-menu__notitie">Niets gevonden.</div>}
      {notitie && <div className="s-menu__notitie">{notitie}</div>}
    </div>
  );
}

export function MenuScheiding() {
  return <div className="s-menu__scheiding" role="separator" />;
}
